import { Box, ButtonBase, Typography } from "@mui/material";
import { ReactNode } from "react";
import {
  colorLandingBrand,
  colorLandingBrandLine,
  colorLandingBrandSoft,
  colorLandingBrandStrong,
  colorLandingInk,
  colorLandingLine,
  colorLandingSurface,
  colorLandingTint,
  fontLandingSans,
} from "src/design/tokens";

interface ShowcaseTabProps {
  label: string;
  icon: ReactNode;
  active: boolean;
  /** Runs the fill animation under the active tab while auto-rotation is live. */
  showProgress: boolean;
  progressDurationMs: number;
  onSelect: () => void;
}

/**
 * One entry of the feature showcase rail. Collapses to an icon-only square on
 * narrow screens, where the rail becomes a horizontal row.
 */
export const ShowcaseTab = ({
  label,
  icon,
  active,
  showProgress,
  progressDurationMs,
  onSelect,
}: ShowcaseTabProps) => (
  <ButtonBase
    role="tab"
    aria-selected={active}
    onClick={onSelect}
    sx={{
      display: "flex",
      gap: 1.625,
      alignItems: "center",
      justifyContent: { xs: "center", sm: "flex-start" },
      textAlign: "left",
      backgroundColor: active ? colorLandingTint : colorLandingSurface,
      border: `1px solid ${active ? colorLandingBrand : colorLandingLine}`,
      borderRadius: "14px",
      px: { xs: 0, sm: 2 },
      py: { xs: 0, sm: 1.875 },
      width: { xs: 54, sm: "100%" },
      height: { xs: 54, sm: "auto" },
      position: "relative",
      overflow: "hidden",
      fontFamily: fontLandingSans,
      transition: "border-color .2s, background .2s",
      "&:hover": { borderColor: active ? colorLandingBrand : colorLandingBrandLine },
    }}
  >
    <Box
      sx={{
        width: 38,
        height: 38,
        borderRadius: "10px",
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
        transition: "background .2s, color .2s",
        backgroundColor: active ? colorLandingBrand : colorLandingBrandSoft,
        color: active ? colorLandingSurface : colorLandingBrandStrong,
      }}
    >
      {icon}
    </Box>
    <Typography
      sx={{
        display: { xs: "none", sm: "block" },
        fontWeight: 700,
        fontSize: 14.5,
        color: colorLandingInk,
      }}
    >
      {label}
    </Typography>
    {active && showProgress && (
      <Box
        sx={{
          display: { xs: "none", sm: "block" },
          position: "absolute",
          left: 0,
          bottom: 0,
          height: 3,
          borderRadius: "0 3px 3px 0",
          backgroundColor: colorLandingBrand,
          animation: `showcaseFill ${progressDurationMs}ms linear forwards`,
          "@keyframes showcaseFill": { from: { width: 0 }, to: { width: "100%" } },
          "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
            width: "100%",
          },
        }}
      />
    )}
  </ButtonBase>
);
